import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import * as MuiIcons from '@mui/icons-material';
import { useCreateAIPortfolio, useUpdateAIPortfolio } from '../services/api';
import { useQueryClient } from '@tanstack/react-query';

const STRATEGIES = [
  { value: 'momentum_short', label: 'Short Momentum', sub: 'Days–2 wks', icon: 'BoltOutlined', cadence: 'daily',
    subcategories: [
      { value: 'momentum_short', label: 'Swing Trading', sub: 'RSI + Bollinger bands' },
      { value: 'momentum_short.earnings', label: 'Earnings', sub: 'Buy before, sell after' },
      { value: 'momentum_short.breakout', label: 'Breakout', sub: 'Volume breakouts' },
    ],
  },
  { value: 'momentum_mid', label: 'Mid Momentum', sub: '2–8 wks', icon: 'TrendingUpOutlined', cadence: 'weekly',
    subcategories: [
      { value: 'momentum_mid', label: 'EMA/MACD', sub: 'Crossovers + pullbacks' },
      { value: 'momentum_mid.sector_rotation', label: 'Sector Rotation', sub: 'Ride sector waves' },
    ],
  },
  { value: 'momentum_long', label: 'Long Momentum', sub: '2–6 mo', icon: 'ShowChartOutlined', cadence: 'biweekly' },
  { value: 'value', label: 'Value', sub: 'Patient', icon: 'SearchOutlined', cadence: 'weekly',
    subcategories: [
      { value: 'value', label: 'Classic Value', sub: 'RSI<50 + fundamentals' },
      { value: 'value.deep_value', label: 'Deep Value', sub: 'Contrarian, down 40%+' },
      { value: 'value.catalyst', label: 'Catalyst', sub: 'Activist, spinoff' },
    ],
  },
  { value: 'growth', label: 'Growth', sub: 'Revenue accel.', icon: 'RocketLaunchOutlined', cadence: 'biweekly',
    subcategories: [
      { value: 'growth', label: 'Growth', sub: 'Revenue accel. + margins' },
      { value: 'growth.garp', label: 'GARP', sub: 'PEG < 1.5' },
      { value: 'growth.hypergrowth', label: 'Hypergrowth', sub: '50%+ revenue' },
    ],
  },
  { value: 'income', label: 'Income', sub: 'Dividends', icon: 'AccountBalanceOutlined', cadence: 'monthly',
    subcategories: [
      { value: 'income', label: 'Yield Focus', sub: 'Stable dividends + low beta' },
      { value: 'income.reit', label: 'REIT', sub: 'Real estate focus' },
      { value: 'income.dividend_growth', label: 'Div. Growth', sub: '10+ yr streaks' },
    ],
  },
];


const STRATEGY_PORTFOLIO_NAMES = {
  momentum_short: 'Short Momentum - Swing Trading',
  'momentum_short.earnings': 'Short Momentum - Earnings Prediction',
  'momentum_short.breakout': 'Short Momentum - Breakout Trading',
  momentum_mid: 'Mid Momentum - EMA/MACD',
  'momentum_mid.sector_rotation': 'Mid Momentum - Sector Rotation',
  momentum_long: 'Long Momentum',
  value: 'Value - Classic',
  'value.deep_value': 'Value - Deep Value',
  'value.catalyst': 'Value - Catalyst',
  growth: 'Growth - Standard',
  'growth.garp': 'Growth - GARP',
  'growth.hypergrowth': 'Growth - Hypergrowth',
  income: 'Income - Yield Focus',
  'income.reit': 'Income - REIT',
  'income.dividend_growth': 'Income - Dividend Growth',
};

const STRATEGY_DESCRIPTIONS = {
  momentum_short: 'Short-Term Momentum: Targets high-volatility small/mid-cap stocks (>2% daily swing) using RSI and Bollinger bands for entry/exit. Holds days to 2 weeks.',
  'momentum_short.earnings': 'Earnings Prediction: Buys before earnings, sells day after release. Uses historical earnings reactions, industry trends, and sentiment analysis to predict market reaction.',
  'momentum_short.breakout': 'Breakout Trading: Catches stocks breaking out of tight consolidation on volume spikes. Uses Bollinger squeeze and resistance breakouts. Holds days to 2 weeks.',
  momentum_mid: 'Mid-Term Momentum: Targets mid-cap stocks with EMA crossover confirmations and >3% weekly swing. Uses MACD and pullbacks to 20dMA. Holds 2-8 weeks.',
  'momentum_mid.sector_rotation': 'Sector Rotation: Rides sector momentum waves. Buys leaders in strong sectors, exits when sector momentum fades. Holds 2-8 weeks.',
  momentum_long: 'Long-Term Momentum: Follows sector rotation and 200dMA trends across large/mid-cap names. Overweights sectors with strong monthly momentum. Holds 2-6 months.',
  value: 'Value: Accumulates undervalued large/mid-cap stocks near support levels. Requires RSI<50 and strong fundamentals. Patient approach, avoids chasing.',
  'value.deep_value': 'Deep Value: Contrarian strategy targeting stocks down 40%+ with improving fundamentals. Buys what others are selling. Requires deep oversold + positive FCF.',
  'value.catalyst': 'Catalyst Value: Undervalued stocks with identifiable upcoming catalysts (activist, spinoff, restructuring). Buys before re-rating event.',
  growth: 'Growth: Rides high-growth mid-cap companies with accelerating revenue. Enters on pullbacks within uptrends. Tolerates higher valuations for proven growth.',
  'growth.garp': 'GARP: Growth at Reasonable Price. Targets 15-40% revenue growth with PEG < 1.5. Quality growth with valuation discipline.',
  'growth.hypergrowth': 'Hypergrowth: High-risk/high-reward disruptors with 50%+ revenue growth. Tolerates high valuations for proven market expansion.',
  income: 'Income: Prioritizes stable large-cap dividend-paying companies with low beta. Buy-and-hold for qualified dividends. Focuses on yield sustainability.',
  'income.reit': 'REIT Income: Focuses on REITs trading below NAV with strong FFO yield and occupancy. Diversifies across residential, industrial, healthcare, data center sub-sectors.',
  'income.dividend_growth': 'Dividend Growth: Targets companies with 10+ years of consecutive dividend increases. Low payout ratios, strong FCF. Compounding dividends over time.',
};

// Default drawdown per strategy — synced when strategy changes
const STRATEGY_DEFAULT_DRAWDOWN = {
  momentum_short: 35,
  'momentum_short.earnings': 35,
  'momentum_short.breakout': 35,
  momentum_mid: 25,
  'momentum_mid.sector_rotation': 25,
  momentum_long: 20,
  value: 15,
  'value.deep_value': 20,
  'value.catalyst': 20,
  growth: 25,
  'growth.garp': 20,
  'growth.hypergrowth': 40,
  income: 10,
  'income.reit': 15,
  'income.dividend_growth': 10,
};

// Trading style + exit defaults per strategy
const STRATEGY_TRADING_DEFAULTS = {
  momentum_short: {
    trading_style: 'active',
    stop_loss_pct: 5,
    take_profit_pct: 10,
    stale_days: 5,
  },
  'momentum_short.earnings': {
    trading_style: 'active',
    stop_loss_pct: 8,
    take_profit_pct: 12,
    stale_days: null, // forced exit after earnings
  },
  'momentum_short.breakout': {
    trading_style: 'balanced',
    stop_loss_pct: 5,
    take_profit_pct: 12,
    stale_days: 5,
  },
  momentum_mid: {
    trading_style: 'active',
    stop_loss_pct: 8,
    take_profit_pct: 18,
    stale_days: 15,
  },
  'momentum_mid.sector_rotation': {
    trading_style: 'active',
    stop_loss_pct: 10,
    take_profit_pct: 20,
    stale_days: 20,
  },
  momentum_long: {
    trading_style: 'balanced',
    stop_loss_pct: 12,
    take_profit_pct: 30,
    stale_days: 45,
  },
  value: {
    trading_style: 'patient',
    stop_loss_pct: 15,
    take_profit_pct: null, // thesis-based
    stale_days: null,
  },
  'value.deep_value': {
    trading_style: 'patient',
    stop_loss_pct: 20,
    take_profit_pct: null,
    stale_days: null,
  },
  'value.catalyst': {
    trading_style: 'active',
    stop_loss_pct: 15,
    take_profit_pct: 25,
    stale_days: 60,
  },
  growth: {
    trading_style: 'balanced',
    stop_loss_pct: 12,
    take_profit_pct: 25,
    stale_days: 30,
  },
  'growth.garp': {
    trading_style: 'patient',
    stop_loss_pct: 10,
    take_profit_pct: 20,
    stale_days: 30,
  },
  'growth.hypergrowth': {
    trading_style: 'active',
    stop_loss_pct: 15,
    take_profit_pct: 40,
    stale_days: 20,
  },
  income: {
    trading_style: 'patient',
    stop_loss_pct: 10,
    take_profit_pct: null, // hold for dividends
    stale_days: null,
  },
  'income.reit': {
    trading_style: 'patient',
    stop_loss_pct: 12,
    take_profit_pct: null,
    stale_days: null,
  },
  'income.dividend_growth': {
    trading_style: 'patient',
    stop_loss_pct: 10,
    take_profit_pct: null,
    stale_days: null,
  },
};

const TRADING_STYLES = [
  { value: 'active', label: 'Active', desc: 'Deploy on fear/dips, quick profit-taking, tight stops' },
  { value: 'balanced', label: 'Balanced', desc: 'Wait for confirmation, moderate targets' },
  { value: 'patient', label: 'Patient', desc: 'Wait for value + setup, hold for thesis' },
];

function getTradingDefaults(strategy) {
  const baseKey = strategy.includes('.') ? strategy.split('.')[0] : strategy;
  return STRATEGY_TRADING_DEFAULTS[strategy] || STRATEGY_TRADING_DEFAULTS[baseKey] || STRATEGY_TRADING_DEFAULTS.growth;
}

const TAX_TREATMENTS = [
  { value: 'tax_agnostic', label: 'Tax-Agnostic', description: "Use strategy's natural cadence" },
  { value: 'short_term_friendly', label: 'Short-Term Friendly', description: 'OK with short-term capital gains' },
  { value: 'long_term_optimized', label: 'Long-Term Optimized', description: 'Hold 12+ months, monthly minimum rebalance' },
];

const DRAWDOWN_LABELS = {
  5: 'Very conservative',
  10: 'Conservative',
  15: 'Moderate',
  20: 'Moderate-aggressive',
  25: 'Aggressive',
  30: 'Very aggressive',
  40: 'High risk',
  50: 'Maximum risk',
};

const getDrawdownLabel = (val) => {
  const keys = Object.keys(DRAWDOWN_LABELS).map(Number).sort((a, b) => a - b);
  for (let i = keys.length - 1; i >= 0; i--) {
    if (val >= keys[i]) return DRAWDOWN_LABELS[keys[i]];
  }
  return 'Very conservative';
};

function parseDrawdownFromRiskTolerance(riskTolerance) {
  const match = riskTolerance?.match(/^max_drawdown_(\d+)pct$/);
  if (match) return Number(match[1]);
  if (riskTolerance === 'conservative') return 10;
  if (riskTolerance === 'aggressive') return 25;
  return 15;
}

function buildFormFromPortfolio(p) {
  const constraints = typeof p.constraints === 'string' ? JSON.parse(p.constraints || '{}') : (p.constraints || {});
  // Map legacy 'momentum' to 'momentum_mid', preserve dot-notation subcategories
  let strategy = p.strategy || 'growth';
  if (strategy === 'momentum') strategy = 'momentum_mid';
  // Validate the strategy key exists in our config (base or subcategory)
  const baseKey = strategy.includes('.') ? strategy.split('.')[0] : strategy;
  if (!STRATEGIES.find(s => s.value === baseKey)) strategy = 'growth';
  const tradingDefaults = getTradingDefaults(strategy);
  // Map legacy entry_mode to trading_style
  let tradingStyle = constraints.trading_style || constraints.entry_mode;
  if (tradingStyle === 'opportunistic') tradingStyle = 'active';
  else if (tradingStyle === 'confirmed') tradingStyle = 'balanced';
  return {
    name: p.name || '',
    strategy,
    starting_capital: p.starting_capital || 10000,
    max_drawdown_pct: constraints.max_drawdown_pct || parseDrawdownFromRiskTolerance(p.risk_tolerance),
    tax_treatment: constraints.tax_treatment || 'tax_agnostic',
    max_position_pct: constraints.max_position_pct || 15,
    max_positions: constraints.max_positions || 15,
    user_instructions: constraints.user_instructions || '',
    // Trading config
    trading_style: tradingStyle || tradingDefaults.trading_style,
    stop_loss_pct: constraints.stop_loss_pct ?? tradingDefaults.stop_loss_pct,
    take_profit_pct: constraints.take_profit_pct ?? tradingDefaults.take_profit_pct,
    stale_days: constraints.stale_days ?? tradingDefaults.stale_days,
  };
}

const DEFAULT_FORM = {
  name: '',
  strategy: 'growth',
  starting_capital: 10000,
  max_drawdown_pct: 15,
  tax_treatment: 'tax_agnostic',
  max_position_pct: 15,
  max_positions: 10,
  user_instructions: '',
  // Trading config defaults (will sync with strategy)
  trading_style: 'balanced',
  stop_loss_pct: 12,
  take_profit_pct: 25,
  stale_days: 30,
};

const AIPortfolioCreateModal = ({ isOpen, onClose, onSuccess, loggedInUser, editPortfolio }) => {
  const isEdit = !!editPortfolio;
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const createMutation = useCreateAIPortfolio();
  const updateMutation = useUpdateAIPortfolio();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (isOpen) {
      setFormData(editPortfolio ? buildFormFromPortfolio(editPortfolio) : DEFAULT_FORM);
      setError(null);
    }
  }, [isOpen, editPortfolio]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const constraints = {
        max_position_pct: Number(formData.max_position_pct),
        max_positions: Number(formData.max_positions),
        max_drawdown_pct: Number(formData.max_drawdown_pct),
        tax_treatment: formData.tax_treatment,
        user_instructions: formData.user_instructions || undefined,
        // Trading config
        trading_style: formData.trading_style,
        stop_loss_pct: formData.stop_loss_pct,
        take_profit_pct: formData.take_profit_pct,
        stale_days: formData.stale_days,
      };

      if (isEdit) {
        const payload = {
          id: editPortfolio.id,
          name: formData.name,
          strategy: formData.strategy,
          risk_tolerance: `max_drawdown_${formData.max_drawdown_pct}pct`,
          constraints,
        };
        await updateMutation.mutateAsync(payload);
        queryClient.invalidateQueries(['aiPortfolioHoldings', editPortfolio.id]);
        queryClient.invalidateQueries(['aiPortfolios']);
      } else {
        const payload = {
          owner_id: loggedInUser?.email,
          name: formData.name,
          strategy: formData.strategy,
          starting_capital: Number(formData.starting_capital),
          risk_tolerance: `max_drawdown_${formData.max_drawdown_pct}pct`,
          constraints,
        };
        const result = await createMutation.mutateAsync(payload);
        queryClient.invalidateQueries(['aiPortfolios']);
        onSuccess(result);
      }
      onClose();
    } catch (err) {
      setError(err.message || `Failed to ${isEdit ? 'update' : 'create'} portfolio`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      // Auto-populate name + AI instructions + trading config when strategy changes
      if (field === 'strategy') {
        const baseKey = value.includes('.') ? value.split('.')[0] : value;
        if (!isEdit) {
          const desc = STRATEGY_DESCRIPTIONS[value] || STRATEGY_DESCRIPTIONS[baseKey] || '';
          if (!prev.user_instructions || Object.values(STRATEGY_DESCRIPTIONS).includes(prev.user_instructions)) {
            next.user_instructions = desc;
          }
          const autoName = STRATEGY_PORTFOLIO_NAMES[value] || STRATEGY_PORTFOLIO_NAMES[baseKey] || '';
          if (!prev.name || Object.values(STRATEGY_PORTFOLIO_NAMES).includes(prev.name)) {
            next.name = autoName;
          }
        }
        // Sync drawdown and trading config with strategy
        const defaultDrawdown = STRATEGY_DEFAULT_DRAWDOWN[value] || STRATEGY_DEFAULT_DRAWDOWN[baseKey];
        if (defaultDrawdown) next.max_drawdown_pct = defaultDrawdown;
        const tradingDefaults = getTradingDefaults(value);
        next.trading_style = tradingDefaults.trading_style;
        next.stop_loss_pct = tradingDefaults.stop_loss_pct;
        next.take_profit_pct = tradingDefaults.take_profit_pct;
        next.stale_days = tradingDefaults.stale_days;
      }
      return next;
    });
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={isSubmitting ? () => {} : onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div style={{ position: 'fixed', inset: 0, overflowY: 'auto' }}>
          <div style={{ display: 'flex', minHeight: '100%', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', width: '100%', maxWidth: '40rem', borderRadius: '1rem', backgroundColor: 'white', boxShadow: '0 25px 50px -12px rgba(0,0,0,.25)', textAlign: 'left' }}>
                <div style={{ flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.5rem', paddingBottom: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>
                  <Dialog.Title as="h3" className="text-xl font-semibold leading-6 text-gray-900 flex items-center gap-2">
                    <MuiIcons.SmartToy className="text-blue-500" />
                    {isEdit ? 'Edit Portfolio Settings' : 'Create AI Managed Portfolio'}
                  </Dialog.Title>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-600" disabled={isSubmitting}>
                    <MuiIcons.Close />
                  </button>
                </div>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
                  <div style={{ padding: '1.5rem', paddingTop: '1rem', overflowY: 'auto', maxHeight: '65vh' }} className="space-y-4">
                    {/* Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Portfolio Name</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => updateField('name', e.target.value)}
                        placeholder="My Growth Portfolio"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        required
                      />
                    </div>

                    {/* Strategy */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Strategy</label>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
                        {STRATEGIES.map(s => {
                          const baseKey = formData.strategy.includes('.') ? formData.strategy.split('.')[0] : formData.strategy;
                          const selected = baseKey === s.value;
                          const Icon = MuiIcons[s.icon] || MuiIcons.TrendingUp;
                          return (
                            <button
                              key={s.value}
                              type="button"
                              onClick={() => updateField('strategy', s.value)}
                              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.625rem', borderRadius: '0.5rem', border: selected ? '2px solid #3b82f6' : '1px solid #e5e7eb', backgroundColor: selected ? '#eff6ff' : '#fff', cursor: 'pointer', transition: 'all 150ms', textAlign: 'left' }}
                            >
                              <Icon style={{ fontSize: 18, color: selected ? '#3b82f6' : '#9ca3af', flexShrink: 0 }} />
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: selected ? '#1d4ed8' : '#374151', lineHeight: 1.2 }}>{s.label}</div>
                                <div style={{ fontSize: '0.625rem', color: selected ? '#60a5fa' : '#9ca3af', lineHeight: 1.2 }}>{s.sub}</div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      {/* Subcategory selector — shown when parent has subcategories */}
                      {(() => {
                        const baseKey = formData.strategy.includes('.') ? formData.strategy.split('.')[0] : formData.strategy;
                        const parentStrat = STRATEGIES.find(s => s.value === baseKey);
                        if (!parentStrat?.subcategories) return null;
                        return (
                          <div style={{ display: 'flex', gap: '0.375rem', marginTop: '0.5rem', padding: '0.375rem', backgroundColor: '#f9fafb', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
                            {parentStrat.subcategories.map(sub => {
                              const isActive = formData.strategy === sub.value;
                              return (
                                <button
                                  key={sub.value}
                                  type="button"
                                  onClick={() => updateField('strategy', sub.value)}
                                  style={{ flex: 1, padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: isActive ? '1px solid #3b82f6' : '1px solid transparent', backgroundColor: isActive ? '#eff6ff' : 'transparent', cursor: 'pointer', transition: 'all 150ms', textAlign: 'center' }}
                                >
                                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: isActive ? '#1d4ed8' : '#6b7280' }}>{sub.label}</div>
                                  <div style={{ fontSize: '0.625rem', color: isActive ? '#60a5fa' : '#9ca3af' }}>{sub.sub}</div>
                                </button>
                              );
                            })}
                          </div>
                        );
                      })()}
                      {/* AI Instructions — inline under strategy */}
                      <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] font-semibold text-blue-600 uppercase tracking-wider flex items-center gap-1">
                            <MuiIcons.AutoAwesome style={{ fontSize: 12 }} /> AI Instructions
                          </span>
                          <span className="text-[10px] text-blue-400">editable</span>
                        </div>
                        <textarea
                          value={formData.user_instructions}
                          onChange={(e) => updateField('user_instructions', e.target.value)}
                          placeholder="Strategy instructions for the AI, plus any custom preferences..."
                          rows={2}
                          className="w-full px-2 py-1.5 border border-blue-200 rounded bg-white text-sm text-gray-700 focus:ring-1 focus:ring-blue-400 focus:border-blue-400 resize-none"
                        />
                      </div>
                    </div>

                    {/* Max Drawdown */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max willing to lose: {formData.max_drawdown_pct}%
                        <span className="text-gray-400 font-normal ml-1">({getDrawdownLabel(formData.max_drawdown_pct)})</span>
                      </label>
                      <input
                        type="range" min="5" max="50" step="5"
                        value={formData.max_drawdown_pct}
                        onChange={(e) => updateField('max_drawdown_pct', Number(e.target.value))}
                        className="w-full"
                      />
                      <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                        <span>5%</span><span>25%</span><span>50%</span>
                      </div>
                    </div>

                    {/* Tax Treatment */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tax Treatment</label>
                      <select
                        value={formData.tax_treatment}
                        onChange={(e) => updateField('tax_treatment', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        {TAX_TREATMENTS.map(t => (
                          <option key={t.value} value={t.value}>{t.label} — {t.description}</option>
                        ))}
                      </select>
                    </div>

                    {/* Trading Config - Entry/Exit/Cash */}
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center gap-1.5 mb-2">
                        <MuiIcons.TuneOutlined style={{ fontSize: 16, color: '#6b7280' }} />
                        <span className="text-sm font-medium text-gray-700">Trading Rules</span>
                        <span className="text-[10px] text-gray-400 ml-auto">AI uses as guidance</span>
                      </div>

                      {/* Trading Style */}
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Trading Style</label>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          {TRADING_STYLES.map(s => {
                            const isActive = formData.trading_style === s.value;
                            return (
                              <button
                                key={s.value}
                                type="button"
                                onClick={() => updateField('trading_style', s.value)}
                                style={{
                                  flex: 1,
                                  padding: '0.375rem 0.5rem',
                                  borderRadius: '0.25rem',
                                  fontSize: '0.75rem',
                                  border: isActive ? '1px solid #3b82f6' : '1px solid #e5e7eb',
                                  backgroundColor: isActive ? '#eff6ff' : '#fff',
                                  color: isActive ? '#1d4ed8' : '#4b5563',
                                  cursor: 'pointer',
                                  transition: 'all 150ms',
                                  fontWeight: 500,
                                }}
                              >
                                {s.label}
                              </button>
                            );
                          })}
                        </div>
                        <div className="text-[10px] text-gray-400 mt-0.5">
                          {TRADING_STYLES.find(s => s.value === formData.trading_style)?.desc}
                        </div>
                      </div>

                      {/* Stop Loss & Take Profit */}
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Stop Loss: {formData.stop_loss_pct ? `~${formData.stop_loss_pct}%` : 'None'}
                          </label>
                          <input
                            type="range" min="0" max="25" step="1"
                            value={formData.stop_loss_pct || 0}
                            onChange={(e) => updateField('stop_loss_pct', Number(e.target.value) || null)}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Take Profit: {formData.take_profit_pct ? `~${formData.take_profit_pct}%` : 'Thesis-based'}
                          </label>
                          <input
                            type="range" min="0" max="50" step="5"
                            value={formData.take_profit_pct || 0}
                            onChange={(e) => updateField('take_profit_pct', Number(e.target.value) || null)}
                            className="w-full"
                          />
                        </div>
                      </div>

                      {/* Stale Exit */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Stale Exit: {formData.stale_days ? `~${formData.stale_days} days` : 'None (hold indefinitely)'}
                        </label>
                        <input
                          type="range" min="0" max="60" step="5"
                          value={formData.stale_days || 0}
                          onChange={(e) => updateField('stale_days', Number(e.target.value) || null)}
                          className="w-full"
                        />
                        <div className="text-[10px] text-gray-400 mt-0.5">
                          Review positions with no meaningful movement after this period
                        </div>
                      </div>
                    </div>

                    {/* Capital & Position Sizing */}
                    {!isEdit && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Starting Capital</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                          <input
                            type="number"
                            value={formData.starting_capital}
                            onChange={(e) => updateField('starting_capital', e.target.value)}
                            min="1000"
                            step="1000"
                            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
                            required
                          />
                        </div>
                      </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Max Position: {formData.max_position_pct}%
                        </label>
                        <input
                          type="range" min="5" max="100" step="5"
                          value={formData.max_position_pct}
                          onChange={(e) => updateField('max_position_pct', e.target.value)}
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Max Positions: {formData.max_positions}
                        </label>
                        <input
                          type="range" min="1" max="10" step="1"
                          value={formData.max_positions}
                          onChange={(e) => updateField('max_positions', Number(e.target.value))}
                          style={{ width: '100%' }}
                        />
                      </div>
                    </div>

                    {error && (
                      <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>
                    )}
                  </div>

                  <div style={{ padding: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb', flexShrink: 0 }}>
                    <button
                      type="submit"
                      disabled={isSubmitting || !formData.name}
                      style={{ width: '100%', padding: '0.75rem', backgroundColor: (isSubmitting || !formData.name) ? '#93c5fd' : '#2563eb', color: 'white', borderRadius: '0.5rem', fontWeight: 500, border: 'none', cursor: (isSubmitting || !formData.name) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontSize: '1rem' }}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                          {isEdit ? 'Saving...' : 'AI is generating allocation...'}
                        </>
                      ) : (
                        <>
                          <MuiIcons.AutoAwesome fontSize="small" />
                          {isEdit ? 'Save Settings' : 'Create Portfolio'}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AIPortfolioCreateModal;
